import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChild, ElementRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GridModule, GridDataResult, PageChangeEvent, SortSettings } from '@progress/kendo-angular-grid';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IconsModule } from '@progress/kendo-angular-icons';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { SortDescriptor } from '@progress/kendo-data-query';
import { forkJoin } from 'rxjs';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import { FileVaultItem, FileSearchCriteria, PagedResult, ApplicationFileInfo, FileHistory } from '../../models/file-vault.models';
import { KENDO_ICONS } from "@progress/kendo-angular-icons";

export type ListFileAction = 'download' | 'view' | 'history' | 'checkout' | 'checkin' | 'delete' | 'toggle-global';

@Component({
  selector: 'fv-file-list',
  standalone: true,
  template: `
    <div class="file-list-container">
      <div class="toolbar" *ngIf="showToolbar">
        <input type="text" class="search-input" placeholder="Search files..."
          [value]="searchText" (input)="onSearchInput($event)" (keyup.enter)="search()" />
        <button kendoButton (click)="search()">Search</button>
        <button kendoButton (click)="refresh()">Refresh</button>
      </div>
      
      <kendo-grid
        [data]="gridData"
        [pageSize]="pageSize"
        [skip]="skip"
        [pageable]="true"
        [sortable]="sortSettings"
        [sort]="sort"
        [loading]="isLoading"
        [selectable]="{ mode: 'single' }"
        (pageChange)="onPageChange($event)"
        (sortChange)="onSortChange($event)"
        (selectionChange)="onRowSelect($event)"
        class="file-grid">
        
        <kendo-grid-column field="fileName" title="Name" [width]="300">
          <ng-template kendoGridCellTemplate let-dataItem>
            <div class="file-name-cell">
              <span class="file-icon">{{ getFileIcon(dataItem) }}</span>
              <span class="file-name" [title]="dataItem.fileName">{{ dataItem.fileName }}</span>
              <span class="global-badge" *ngIf="dataItem.isGlobalView" title="Shared across applications">G</span>
            </div>
          </ng-template>
        </kendo-grid-column>
        
        <kendo-grid-column field="applicationTypeName" title="Source" [width]="120"
          *ngIf="isApplicationFileMode && visibleApplicationTypeIds?.length">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="app-type-badge" 
              [class.owner]="dataItem.applicationTypeId === applicationTypeId"
              [class.external]="dataItem.applicationTypeId !== applicationTypeId">
              {{ dataItem.applicationTypeName || 'Unknown' }}
            </span>
          </ng-template>
        </kendo-grid-column>
        
        <kendo-grid-column field="fileExtension" title="Type" [width]="100">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.fileExtension?.toUpperCase() || '-' }}
          </ng-template>
        </kendo-grid-column>
        
        <kendo-grid-column field="fileSize" title="Size" [width]="120">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ formatFileSize(dataItem.fileSize) }}
          </ng-template>
        </kendo-grid-column>
        
        <kendo-grid-column field="modifiedAt" title="Modified" [width]="180">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.modifiedAt | date:'medium' }}
          </ng-template>
        </kendo-grid-column>
        
        <kendo-grid-column field="modifiedBy" title="Modified By" [width]="150">
        </kendo-grid-column>
        
        <kendo-grid-column field="version" title="Version" [width]="80">
          <ng-template kendoGridCellTemplate let-dataItem>
            v{{ dataItem.version }}
          </ng-template>
        </kendo-grid-column>
        
        <kendo-grid-column title="Actions" [width]="230" *ngIf="showActions">
          <ng-template kendoGridCellTemplate let-dataItem>
            <div class="action-buttons">
              <!-- Download -->
              <button kendoButton fillMode="flat" (click)="onAction('download', dataItem)"
                title="Download" aria-label="Download">
                <kendo-icon name="download"></kendo-icon>
              </button>
              <!-- View in browser -->
              <button kendoButton fillMode="flat" (click)="onAction('view', dataItem)"
                title="View" aria-label="View">
                <kendo-icon name="eye"></kendo-icon>
              </button>
              <!-- Version history -->
              <button kendoButton fillMode="flat" (click)="onAction('history', dataItem)"
                title="Version History" aria-label="Version History">
                <kendo-icon name="clock-arrow-rotate"></kendo-icon>
              </button>
              <!-- Check out (lock) -->
              <button kendoButton fillMode="flat" (click)="onAction('checkout', dataItem)"
                title="Check Out" aria-label="Check Out"
                *ngIf="showCheckout && !dataItem.isLocked">
                <kendo-icon name="lock"></kendo-icon>
              </button>
              <!-- Check in (upload new version) -->
              <button kendoButton fillMode="flat" (click)="onAction('checkin', dataItem)"
                title="Check In" aria-label="Check In"
                *ngIf="showCheckout && dataItem.isLocked">
                <kendo-icon name="upload"></kendo-icon>
              </button>
              <!-- Toggle global visibility (ApplicationFile mode only) -->
              <button kendoButton fillMode="flat" (click)="onAction('toggle-global', dataItem)"
                [title]="dataItem.isGlobalView ? 'Remove from Global View' : 'Add to Global View'"
                [attr.aria-label]="dataItem.isGlobalView ? 'Remove from Global View' : 'Add to Global View'"
                [style.color]="dataItem.isGlobalView ? '#155724' : '#6c757d'"
                *ngIf="dataItem._applicationFileId">
                <kendo-icon [name]="dataItem.isGlobalView ? 'globe' : 'globe-outline'"></kendo-icon>
              </button>
              <!-- Delete -->
              <button kendoButton fillMode="flat" (click)="onAction('delete', dataItem)"
                title="Delete" aria-label="Delete"
                class="btn-danger">
                <kendo-icon name="trash"></kendo-icon>
              </button>
            </div>
          </ng-template>
        </kendo-grid-column>
      </kendo-grid>

      <!-- Hidden file input for check-in flow -->
      <input type="file" #checkinFileInput style="display: none"
        (change)="onCheckinFileSelected($event)" />
    </div>
  `,
  styles: [`
    .file-list-container { height: 100%; display: flex; flex-direction: column; }
    .toolbar { display: flex; gap: 8px; padding: 12px; background: var(--fv-surface-hover); border-bottom: 1px solid var(--fv-border); }
    .search-input { flex: 1; padding: 8px 12px; border: 1px solid var(--fv-border); border-radius: 4px; font-size: 14px; }
    .file-grid { flex: 1; }
    .file-name-cell { display: flex; align-items: center; gap: 8px; }
    .file-icon { font-size: 16px; }
    .file-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .action-buttons { display: flex; gap: 2px; align-items: center; }
    .action-buttons button { padding: 4px 6px; min-width: 28px; }
    .action-buttons button.btn-danger { color: var(--fv-status-error-fg); }
    .action-buttons button.btn-danger:hover { color: var(--fv-status-error-fg); background: var(--fv-status-error-bg); }
    .app-type-badge { padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: 600; text-transform: uppercase; }
    .app-type-badge.owner { background: var(--fv-status-success-bg); color: var(--fv-status-success-fg); }
    .app-type-badge.external { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .global-badge { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; border-radius: 50%; background: #ffc107; color: #000; font-size: 10px; font-weight: 700; margin-left: 4px; flex-shrink: 0; }
  `],
  imports: [CommonModule, GridModule, ButtonsModule, IconsModule, IndicatorsModule, KENDO_ICONS]
})
export class FileListComponent implements OnInit, OnChanges {
  private api = inject(FileVaultApiService);

  @Input() directoryPath?: string;  // Made optional to allow loading all files
  @Input() showToolbar = true;
  @Input() showActions = true;
  @Input() showCheckout = true;
  @Input() pageSize = 25;
  @Input() initialCriteria: FileSearchCriteria = {};
  @Input() refreshToken?: unknown;

  /**
   * The primary/owner application type ID for this page.
   * All files uploaded under this app type are visible.
   * When set along with targetId, loads files via the ApplicationFile API instead of file search.
   */
  @Input() applicationTypeId?: number;

  /**
   * The primary/owner application type Name (e.g., 'Rta', 'Ejdf').
   * If provided (and applicationTypeId is not), the component will resolve the name to an ID automatically.
   */
  @Input() applicationTypeName?: string;

  /**
   * Additional application type IDs whose files should also be visible,
   * but ONLY where isGlobalView = true on the ApplicationFile record.
   * Example: RTA page (owner=4) passes [2] here to see isGlobalView EJDF files.
   * Example: EJDF page (owner=2) passes [4] here to see isGlobalView RTA files.
   */
  @Input() visibleApplicationTypeIds?: number[];

  /**
   * Additional application type Names whose files should also be visible (isGlobalView = true only).
   * If provided (and visibleApplicationTypeIds is not), the component will resolve the names to IDs automatically.
   * Example: ['Ejdf'] on an RTA page to see globally-visible EJDF files.
   */
  @Input() visibleApplicationTypeNames?: string[];

  /**
   * Target entity ID (e.g., request number).
   * Required when using applicationTypeId to load files via ApplicationFile API.
   */
  @Input() targetId?: number;

  @Output() fileSelected = new EventEmitter<FileVaultItem>();
  @Output() fileAction = new EventEmitter<{ action: ListFileAction; file: FileVaultItem }>();
  @Output() filesLoaded = new EventEmitter<PagedResult<FileVaultItem>>();
  /** Emits when loading via ApplicationFile API mode */
  @Output() applicationFilesLoaded = new EventEmitter<ApplicationFileInfo[]>();
  /** Emits when file history is loaded (via history action button) */
  @Output() fileHistoryLoaded = new EventEmitter<{ file: FileVaultItem; history: FileHistory[] }>();
  /** Emits when a file is deleted */
  @Output() fileDeleted = new EventEmitter<FileVaultItem>();
  /** Emits when a file is checked in */
  @Output() fileCheckedIn = new EventEmitter<FileVaultItem>();
  /** Emits when the IsGlobalView flag is toggled on an ApplicationFile record */
  @Output() globalViewToggled = new EventEmitter<ApplicationFileInfo>();

  @ViewChild('checkinFileInput') checkinFileInput!: ElementRef<HTMLInputElement>;

  gridData: GridDataResult = { data: [], total: 0 };
  skip = 0;
  sort: SortDescriptor[] = [{ field: 'modifiedAt', dir: 'desc' }];
  sortSettings: SortSettings = { mode: 'single', allowUnsort: true };
  isLoading = false;
  searchText = '';

  /** Whether we are in ApplicationFile API mode (true) vs standard file search mode (false) */
  get isApplicationFileMode(): boolean {
    return this.applicationTypeId != null && this.targetId != null;
  }

  ngOnInit(): void {
    this.resolveApplicationTypeNames().then(() => this.loadFiles());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['directoryPath'] && !changes['directoryPath'].firstChange) {
      this.skip = 0;
      this.loadFiles();
    }
    if (changes['refreshToken'] && !changes['refreshToken'].firstChange) {
      this.skip = 0;
      this.loadFiles();
    }
    if (changes['applicationTypeName'] && !changes['applicationTypeName'].firstChange) {
      this.resolveApplicationTypeNames().then(() => {
        this.skip = 0;
        this.loadFiles();
      });
    }
    if (changes['visibleApplicationTypeNames'] && !changes['visibleApplicationTypeNames'].firstChange) {
      this.resolveVisibleApplicationTypeNames().then(() => {
        this.skip = 0;
        this.loadFiles();
      });
    }
    if ((changes['applicationTypeId'] || changes['targetId'] || changes['visibleApplicationTypeIds'])
        && !changes['applicationTypeId']?.firstChange) {
      this.skip = 0;
      this.loadFiles();
    }
  }

  /**
   * Resolves applicationTypeName → applicationTypeId and visibleApplicationTypeNames → visibleApplicationTypeIds.
   */
  private async resolveApplicationTypeNames(): Promise<void> {
    const promises: Promise<void>[] = [];

    if (this.applicationTypeName && !this.applicationTypeId) {
      promises.push(
        this.api.getApplicationTypeByName(this.applicationTypeName).toPromise().then(result => {
          if (result) {
            this.applicationTypeId = result.id;
          }
        }).catch(err => {
          console.error(`Failed to resolve ApplicationType name '${this.applicationTypeName}':`, err);
        })
      );
    }

    if (this.visibleApplicationTypeNames?.length && !this.visibleApplicationTypeIds?.length) {
      promises.push(this.resolveVisibleApplicationTypeNames());
    }

    await Promise.all(promises);
  }

  /**
   * Resolves visibleApplicationTypeNames array → visibleApplicationTypeIds array.
   */
  private async resolveVisibleApplicationTypeNames(): Promise<void> {
    if (!this.visibleApplicationTypeNames?.length) return;

    try {
      const results = await forkJoin(
        this.visibleApplicationTypeNames.map(name => this.api.getApplicationTypeByName(name))
      ).toPromise();

      if (results) {
        this.visibleApplicationTypeIds = results.map(r => r.id);
      }
    } catch (err) {
      console.error('Failed to resolve visible ApplicationType names:', err);
    }
  }

  async loadFiles(): Promise<void> {
    this.isLoading = true;
    try {
      if (this.isApplicationFileMode) {
        // === Application File API mode ===
        // Load files through fv_ApplicationFile with cross-app-type visibility rules
        const files = await this.api.getApplicationFilesWithVisibility({
          ownerApplicationTypeId: this.applicationTypeId!,
          additionalApplicationTypeIds: this.visibleApplicationTypeIds,
          targetId: this.targetId!
        }).toPromise();

        if (files) {
          // Filter by search text client-side (ApplicationFile API doesn't support text search)
          let filtered = files;
          if (this.searchText) {
            const search = this.searchText.toLowerCase();
            filtered = files.filter(f => f.fileName?.toLowerCase().includes(search));
          }

          // Client-side pagination
          const total = filtered.length;
          const paged = filtered.slice(this.skip, this.skip + this.pageSize);

          // Map ApplicationFileInfo to grid-compatible format
          this.gridData = {
            data: paged.map(af => this.mapApplicationFileToGridItem(af)),
            total
          };

          this.applicationFilesLoaded.emit(files);
        }
      } else {
        // === Standard file search mode ===
        const criteria: FileSearchCriteria = {
          ...this.initialCriteria,
          directory: this.directoryPath || undefined,
          fileName: this.searchText || undefined,
          page: Math.floor(this.skip / this.pageSize) + 1,
          pageSize: this.pageSize,
          sortBy: this.sort[0]?.field,
          sortDescending: this.sort[0]?.dir === 'desc'
        };

        const result = await this.api.searchFiles(criteria).toPromise();
        if (result) {
          this.gridData = { data: result.items, total: result.totalCount };
          this.filesLoaded.emit(result);
        }
      }
    } catch (error) {
      console.error('Failed to load files', error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Map an ApplicationFileInfo to a FileVaultItem-compatible object for the grid.
   * Adds extra metadata: applicationTypeName, isGlobalView
   */
  private mapApplicationFileToGridItem(af: ApplicationFileInfo): any {
    return {
      id: af.fileId,
      fileName: af.fileName || 'Unknown',
      applicationTypeName: af.applicationTypeName,
      applicationTypeId: af.applicationTypeId,
      isGlobalView: af.isGlobalView,
      targetId: af.targetId,
      modifiedAt: af.modifiedDate || af.createdAt,
      createdAt: af.createdAt,
      // These fields may not be available from ApplicationFileInfo
      // The grid template will handle missing values gracefully
      fileExtension: af.fileName?.split('.').pop() || '',
      version: 1,
      isLocked: false,
      _applicationFileId: af.id  // Store the junction record ID for unlink operations
    };
  }

  onPageChange(event: PageChangeEvent): void { this.skip = event.skip; this.loadFiles(); }
  onSortChange(sort: SortDescriptor[]): void { this.sort = sort; this.loadFiles(); }
  onSearchInput(event: Event): void { this.searchText = (event.target as HTMLInputElement).value; }
  search(): void { this.skip = 0; this.loadFiles(); }
  refresh(): void { this.loadFiles(); }

  onRowSelect(event: { selectedRows?: { dataItem: FileVaultItem }[] }): void {
    if (event.selectedRows && event.selectedRows.length > 0) {
      this.fileSelected.emit(event.selectedRows[0].dataItem);
    }
  }

  onAction(action: ListFileAction, file: FileVaultItem): void {
    this.fileAction.emit({ action, file });

    // Handle built-in actions
    switch (action) {
      case 'download':
        this.handleDownload(file);
        break;
      case 'view':
        this.handleView(file);
        break;
      case 'history':
        this.handleHistory(file);
        break;
      case 'checkout':
        this.handleCheckout(file);
        break;
      case 'checkin':
        this.handleCheckin(file);
        break;
      case 'delete':
        this.handleDelete(file);
        break;
      case 'toggle-global':
        this.handleToggleGlobal(file);
        break;
    }
  }

  /**
   * Downloads the file blob and triggers a browser save-as dialog.
   */
  private handleDownload(file: FileVaultItem): void {
    const fileId = file.id?.toString();
    if (!fileId) {
      console.error('Cannot download: file has no ID');
      return;
    }
    this.api.downloadFile(fileId).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = file.fileName || 'download';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
      },
      error: (err: unknown) => console.error('Download failed:', err)
    });
  }

  /**
   * Downloads the file blob and opens it in a new browser tab for viewing.
   */
  private handleView(file: FileVaultItem): void {
    const fileId = file.id?.toString();
    if (!fileId) return;
    this.api.downloadFile(fileId).subscribe({
      next: (blob: Blob) => {
        const contentType = file.contentType || this.inferContentType(file.fileName);
        const viewBlob = new Blob([blob], { type: contentType });
        const url = window.URL.createObjectURL(viewBlob);
        window.open(url, '_blank');
        // Revoke after a delay to allow the tab to load
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      },
      error: (err: unknown) => console.error('View failed:', err)
    });
  }

  /**
   * Fetches the file's version history and emits it via fileHistoryLoaded.
   */
  private handleHistory(file: FileVaultItem): void {
    const fileId = file.id?.toString();
    if (!fileId) return;
    this.api.getFileHistory(fileId).subscribe({
      next: (history: FileHistory[]) => {
        this.fileHistoryLoaded.emit({ file, history });
      },
      error: (err: unknown) => console.error('Failed to load history:', err)
    });
  }

  /**
   * Checks out the file and refreshes the grid.
   */
  private handleCheckout(file: FileVaultItem): void {
    const fileId = file.id?.toString();
    if (!fileId) return;
    this.api.checkOutFile(fileId).subscribe({
      next: () => this.loadFiles(),
      error: (err: unknown) => console.error('Checkout failed:', err)
    });
  }

  /** Tracks which file is being checked in so the file input callback can reference it */
  private checkinTargetFile?: FileVaultItem;

  /**
   * Opens the hidden file input so the user can select a file to check in.
   */
  private handleCheckin(file: FileVaultItem): void {
    this.checkinTargetFile = file;
    this.checkinFileInput?.nativeElement?.click();
  }

  /**
   * Called when the user selects a file from the hidden check-in file input.
   */
  onCheckinFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectedFile = input.files?.[0];
    const targetFile = this.checkinTargetFile;
    // Reset input so the same file can be selected again
    input.value = '';
    this.checkinTargetFile = undefined;

    if (!selectedFile || !targetFile) return;

    const fileId = targetFile.id?.toString();
    if (!fileId) return;

    this.api.checkInFile(fileId, selectedFile).subscribe({
      next: (updated: FileVaultItem) => {
        this.fileCheckedIn.emit(updated);
        this.loadFiles();
      },
      error: (err: unknown) => console.error('Checkin failed:', err)
    });
  }

  /**
   * Soft-deletes the file and refreshes the grid.
   */
  private handleDelete(file: FileVaultItem): void {
    const fileId = file.id?.toString();
    if (!fileId) return;
    this.api.deleteFile(fileId).subscribe({
      next: () => {
        this.fileDeleted.emit(file);
        this.loadFiles();
      },
      error: (err: unknown) => console.error('Delete failed:', err)
    });
  }

  private handleToggleGlobal(file: any): void {
    const appFileId = file._applicationFileId as number;
    if (!appFileId) return;
    this.api.toggleApplicationFileGlobalView(appFileId).subscribe({
      next: (updated: ApplicationFileInfo) => {
        // Update in-place for instant visual feedback without a full reload
        file.isGlobalView = updated.isGlobalView;
        this.globalViewToggled.emit(updated);
      },
      error: (err: unknown) => console.error('Toggle global view failed:', err)
    });
  }

  /**
   * Infers a MIME content type from the file name for in-browser viewing.
   */
  private inferContentType(fileName: string): string {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    const mimeMap: Record<string, string> = {
      'pdf': 'application/pdf',
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'gif': 'image/gif',
      'bmp': 'image/bmp',
      'webp': 'image/webp',
      'svg': 'image/svg+xml',
      'txt': 'text/plain',
      'html': 'text/html',
      'htm': 'text/html',
      'xml': 'text/xml',
      'json': 'application/json',
      'csv': 'text/csv',
      'mp4': 'video/mp4',
      'mp3': 'audio/mpeg',
    };
    return mimeMap[ext || ''] || 'application/octet-stream';
  }

  formatFileSize(bytes: number | undefined): string {
    if (!bytes) return '-';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let unitIndex = 0; let size = bytes;
    while (size >= 1024 && unitIndex < units.length - 1) { size /= 1024; unitIndex++; }
    return size.toFixed(unitIndex > 0 ? 1 : 0) + ' ' + units[unitIndex];
  }

  getFileIcon(file: FileVaultItem): string {
    const ext = file.fileExtension?.toLowerCase();
    const iconMap: Record<string, string> = {
      'pdf': '', 'doc': '', 'docx': '', 'xls': '', 'xlsx': '',
      'ppt': '', 'pptx': '', 'jpg': '', 'jpeg': '', 'png': '',
      'gif': '', 'zip': '', 'txt': ''
    };
    return iconMap[ext || ''] || '';
  }
}
